import { createSelector } from '@reduxjs/toolkit';
import { find, get, isEmpty, isNil, sumBy } from 'lodash';
import { isBefore } from 'date-fns';
import {
  filterOutOptionalExtrasFromFerryReservation,
  getReservationsByProductType,
} from 'utils/helpers';
import { getDateObjectFromReservation } from 'utils/helpers/reservations';
import { getTotalGiftCardAmount } from '../checkout';
import { getPassengerTypes } from '../types';

export const getCurrentBooking = (state) => state.booking?.current || {};
export const getOriginalBooking = (state) => state.booking?.original || {};
export const getUpcomingBooking = (state) => state.booking?.upcoming || {};

export const isBookingOneWay = createSelector(
  getCurrentBooking,
  (booking) =>
    get(
      booking,
      ['reservations_attributes', 0, 'inverse_reservation_id'],
      false // if we can't access this path, we dont want to assume its one way
    ) === null
);

export const getAmountToPay = createSelector(
  getCurrentBooking,
  (state) => state.checkout.paymentAmount,
  getTotalGiftCardAmount,
  (booking, paymentAmount, giftCardAmount) => {
    let amount = booking.deposit_outstanding_in_cents || 0;

    if (paymentAmount === 'balance') {
      amount = booking.balance_in_cents || 0;
    }

    return amount - giftCardAmount;
  }
);

export const getCommissionAmount = createSelector(
  getCurrentBooking,
  (booking) => booking.commission_in_cents
);

export const getBookingPaymentTypes = createSelector(
  getCurrentBooking,
  (booking) => booking.payment_types_attributes || []
);

export const getPayPalPayment = createSelector(
  getBookingPaymentTypes,
  (paymentTypes) =>
    paymentTypes.find(
      (paymentType) =>
        paymentType.name === 'PayPal' && paymentType.gateway === 'braintree'
    )
);

export const getApplePayPayment = createSelector(
  getBookingPaymentTypes,
  (paymentTypes) =>
    paymentTypes.find(
      (paymentType) => paymentType.payment_method === 'apple_pay'
    )
);

export const getGooglePayPayment = createSelector(
  getBookingPaymentTypes,
  (paymentTypes) =>
    paymentTypes.find(
      (paymentType) => paymentType.payment_method === 'google_pay'
    )
);

export const getCreditCardPayment = createSelector(
  getBookingPaymentTypes,
  (paymentTypes) =>
    paymentTypes.find(
      (paymentType) => paymentType.payment_method === 'credit_card'
    )
);

export const getGiftCardPayment = createSelector(
  getBookingPaymentTypes,
  (paymentTypes) =>
    paymentTypes.find(
      (paymentType) => paymentType.gateway === 'national_giftcard'
    )
);

export const getPaymentTypeId = (state) => state.checkout.paymentTypeId;

// booking.surcharge_in_cents represents surcharges that HAVE BEEN PAID
export const getSurchargeInCents = createSelector(
  getCurrentBooking,
  (booking) => booking.surcharge_in_cents
);

export const getSelectedPaymentTypes = createSelector(
  getBookingPaymentTypes,
  getPaymentTypeId,
  (bookingPaymentTypes, paymentTypeId) =>
    bookingPaymentTypes.find(
      (paymentType) => paymentType.id === paymentTypeId
    ) || {}
);

export const getTransactionFee = createSelector(
  getSelectedPaymentTypes,
  (selectedPaymentType) => Number(selectedPaymentType?.transaction_fee || 0)
);

export const getSurchargeAmount = createSelector(
  getAmountToPay,
  getTransactionFee,
  (amountToPay, transactionFee) => {
    let surchargeAmount = 0;
    if (transactionFee !== 0) {
      surchargeAmount = amountToPay * (transactionFee / 100);
    }
    return surchargeAmount;
  }
);

export const getOptionalExtraReservations = createSelector(
  getCurrentBooking,
  (currentBooking) => {
    const { optionalExtras: cmsOptionalExtras } =
      ecom.craftConfig.bookingPreferences.optionalExtras || {};

    if (isEmpty(currentBooking) || !cmsOptionalExtras) {
      return [];
    }

    const optionalExtraRouteIds = cmsOptionalExtras
      .map((item) => item.routeIdOfCoach)
      .filter((item) => !isNil(item));
    const optionalExtraResIds = cmsOptionalExtras
      .map((item) => item.qtResourceId)
      .filter((item) => !isNil(item));

    return currentBooking.reservations_attributes.filter(
      ({ resource_id, route_id }) =>
        optionalExtraResIds.includes(resource_id) ||
        optionalExtraRouteIds.includes(route_id)
    );
  }
);

export const getOptionalExtras = createSelector(
  getCurrentBooking,
  (currentBooking) => {
    const { optionalExtras: cmsOptionalExtras } =
      ecom.craftConfig.bookingPreferences.optionalExtras || {};

    if (isEmpty(currentBooking) || !cmsOptionalExtras) {
      return [];
    }

    const optionalExtraRouteIds = cmsOptionalExtras
      .map((item) => item.routeIdOfCoach)
      .filter((item) => !isNil(item));
    const optionalExtraResIds = cmsOptionalExtras
      .map((item) => item.qtResourceId)
      .filter((item) => !isNil(item));

    const reservations = currentBooking.reservations_attributes;

    const itemsOfResource = reservations
      .filter(({ resource_id }) => optionalExtraResIds.includes(resource_id))
      .map((item) => {
        const {
          resource_id,
          quantity,
          gross_in_cents,
          pre_adjusted_commission_in_cents,
        } = item;
        const name = cmsOptionalExtras.find(
          ({ qtResourceId }) => qtResourceId === resource_id
        ).title;
        return {
          name,
          count: quantity,
          price: gross_in_cents,
          commissionPrice: pre_adjusted_commission_in_cents,
          resourceId: resource_id,
        };
      });

    const itemsOfCoach = reservations
      .filter(({ route_id }) => optionalExtraRouteIds.includes(route_id))
      .map((item) => {
        const {
          route_id,
          passenger_ids,
          gross_in_cents,
          pre_adjusted_commission_in_cents,
        } = item;
        const name = cmsOptionalExtras.find(
          ({ routeIdOfCoach }) => routeIdOfCoach === route_id
        ).title;
        return {
          name,
          count: passenger_ids.length,
          price: gross_in_cents,
          commissionPrice: pre_adjusted_commission_in_cents,
          resourceId: null,
        };
      });
    return [...itemsOfResource, ...itemsOfCoach];
  }
);

/*
  Steps to get prices for each passenger types and vehicles
    1. Get reservation ids
    2. Find each reservation in the reservations_attributes
    3. Sum up each passenger and vehicles
*/
export const getPrices = createSelector(
  getCurrentBooking,
  getPassengerTypes,
  getOptionalExtras,
  (currentBooking, qtPassengerTypes, optionalExtras) => {
    const passengerPrices = {};
    const passengerAdjustments = {};
    let vehiclePrice = 0;
    let commissionPrice = 0;
    let commissionPercentage = 0;
    let ferrySubtotalPrice = 0;

    if (!isEmpty(currentBooking)) {
      // Find each reservation in the reservations_attributes
      let ferryReservations = getReservationsByProductType(
        'ferry,transfer',
        currentBooking
      );
      ferryReservations = filterOutOptionalExtrasFromFerryReservation({
        reservations: ferryReservations,
      });
      // Sum up each passengers and vehicles
      ferryReservations.forEach((reservation) => {
        // Track pre-adjusted totals per passenger type for this reservation
        const reservationPreAdjusted = {};
        const reservationPostAdjusted = {};

        // Sum up passenger prices by type, handling cases where passengers have different prices
        // (e.g., Driver No Charge makes one passenger free)
        reservation.passenger_splits.forEach((split) => {
          const passenger = find(currentBooking.passengers_attributes, {
            id: split.consumer_id,
          });
          if (!passenger) {
            console.warn(
              `[getPrices] Passenger with consumer_id ${split.consumer_id} not found`
            );
            return;
          }
          const passengerType = find(qtPassengerTypes, {
            id: passenger.passenger_type_id,
          });
          if (!passengerType) {
            console.warn(
              `[getPrices] PassengerType with id ${passenger.passenger_type_id} not found`
            );
            return;
          }
          const typeName = passengerType.name;

          if (typeName) {
            const passengerGross =
              reservation.consumer_grosses[split.consumer_id] || 0;
            const passengerPreAdjustedGross = split.gross_in_cents || 0;
            passengerPrices[typeName] =
              (passengerPrices[typeName] || 0) + passengerGross;
            reservationPreAdjusted[typeName] =
              (reservationPreAdjusted[typeName] || 0) +
              passengerPreAdjustedGross;
            reservationPostAdjusted[typeName] =
              (reservationPostAdjusted[typeName] || 0) + passengerGross;
          }
        });

        // Collect negative adjustments (discounts) per passenger type.
        // The API provides adjustments at the reservation level, not per-passenger,
        // so we attribute them to passenger types whose pre/post-adjusted totals differ.
        // Limitation: if multiple types have price differences on the same reservation,
        // all negative adjustments are shown under each affected type.
        if (reservation.adjustments_attributes?.length > 0) {
          const passengerTypesWithAdjustments = Object.keys(
            reservationPreAdjusted
          ).filter(
            (typeName) =>
              reservationPreAdjusted[typeName] !==
              reservationPostAdjusted[typeName]
          );

          passengerTypesWithAdjustments.forEach((typeName) => {
            if (!passengerAdjustments[typeName]) {
              passengerAdjustments[typeName] = [];
            }
            reservation.adjustments_attributes
              .filter((adj) => adj.gross_in_cents < 0)
              .forEach((adj) => {
                passengerAdjustments[typeName].push({
                  description: adj.description,
                  amount: adj.gross_in_cents,
                });
              });
          });
        }

        reservation.vehicle_splits?.forEach((vehicle) => {
          vehiclePrice += reservation.consumer_grosses[vehicle.consumer_id];
        });

        if (!commissionPercentage) {
          commissionPercentage = Number(
            reservation.passenger_splits.find(
              (passenger) => Number(passenger.commission_percentage) !== 0
            )?.commission_percentage || 0
          );
        }

        commissionPrice += reservation.pre_adjusted_commission_in_cents;

        ferrySubtotalPrice += reservation.gross_in_cents;
      });
    }

    let optionalExtraPrice = 0;
    if (!isEmpty(optionalExtras)) {
      optionalExtraPrice = sumBy(optionalExtras, 'price');
      ferrySubtotalPrice += optionalExtraPrice;
      const commissionOfOptionalExtra = sumBy(
        optionalExtras,
        'commissionPrice'
      );
      commissionPrice += commissionOfOptionalExtra;
    }

    // Don't show $0.00 on the pages
    if (ferrySubtotalPrice === 0) {
      ferrySubtotalPrice = undefined;
    }
    if (vehiclePrice === 0) {
      vehiclePrice = undefined;
    }
    if (commissionPrice === 0) {
      commissionPrice = undefined;
    } else {
      ferrySubtotalPrice -= commissionPrice;
    }

    return {
      passengerPrices,
      passengerAdjustments,
      ferrySubtotalPrice,
      vehiclePrice,
      commissionPrice,
      commissionPercentage,
      optionalExtraPrice,
    };
  }
);

export const getGiftVoucherReservations = createSelector(
  getCurrentBooking,
  (booking) => {
    const reservations = booking.reservations_attributes || [];

    return reservations.filter((reservation) =>
      reservation.product_name_underscore.includes('gift_vouchers')
    );
  }
);

export const getNextUpcomingReservation = createSelector(
  getUpcomingBooking,
  (booking) => {
    if (!booking.reservations_attributes?.length) {
      return;
    }

    const now = new Date();
    const upcomingReservation = booking.reservations_attributes.find(
      (reservation) => {
        const travelTime = getDateObjectFromReservation(reservation);

        return isBefore(now, travelTime);
      }
    );

    return upcomingReservation;
  }
);
